/**
 * SupportTicketForm — the shared New Ticket form body (category, severity,
 * subject, rich-text description, related loan/route). Used by both the
 * full-page intake (SupportNewTicketPage) and the header "quick ticket" modal
 * (NewTicketModal) so the two never drift.
 *
 * POST /support_tickets { subject, description(html), category, severity?,
 * relatedDealId? }. Option lists from /support_tickets/meta (static fallback).
 *
 * `relatedDealId` pre-links a loan/route. When `relatedReadOnly`, it renders as
 * a clearable context chip (e.g. the route the header button was pressed from,
 * "/loan/123/documents"); otherwise it's an editable text input.
 *
 * "Feature Request" is an admin-only category (see ADMIN_ONLY_CATEGORIES in
 * supportTicketsApi) — the card only renders for admins, and the severity field
 * relabels to "Priority" when it's selected.
 */
import { useEffect, useMemo, useState } from "react";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CloseRounded from "@mui/icons-material/CloseRounded";
import InputBase from "@mui/material/InputBase";
import LightbulbOutlined from "@mui/icons-material/LightbulbOutlined";
import PersonAddAlt1Outlined from "@mui/icons-material/PersonAddAlt1Outlined";
import ReportProblemOutlined from "@mui/icons-material/ReportProblemOutlined";
import StorageOutlined from "@mui/icons-material/StorageOutlined";
import { useSnackbar } from "notistack";

import HtmlEditor from "../../../components/html/HtmlEditor";
import { usePermissionsHook } from "../../../hooks/usePermissionsHook";
import {
  DEFAULT_SEVERITY,
  FEATURE_REQUEST_CATEGORY,
  SUPPORT_TICKET_SEVERITIES,
  categoriesForUser,
  supportTicketsApi,
} from "../../../api/supportTicketsApi";
import { SEVERITY_META, T } from "./supportUi";

const CATEGORY_CARDS = [
  {
    value: "Create User",
    icon: PersonAddAlt1Outlined,
    desc: "Request a new portal account or access change",
  },
  {
    value: "Issue Report",
    icon: ReportProblemOutlined,
    desc: "Something in the portal isn't working as expected",
  },
  {
    value: "Data Issue",
    icon: StorageOutlined,
    desc: "A loan, document, or figure looks wrong",
  },
  {
    value: FEATURE_REQUEST_CATEGORY,
    icon: LightbulbOutlined,
    desc: "Propose new functionality or an enhancement",
    adminOnly: true,
  },
];

const FieldLabel = ({ children, required, optional }) => (
  <Box sx={{ fontSize: 12.5, fontWeight: 600, color: T.ink700, mb: "7px" }}>
    {children}
    {required && (
      <Box component="span" sx={{ color: T.red, ml: "3px" }}>
        *
      </Box>
    )}
    {optional && (
      <Box
        component="span"
        sx={{ color: T.ink300, fontWeight: 500, ml: "4px" }}
      >
        · optional
      </Box>
    )}
  </Box>
);

const SupportTicketForm = ({
  relatedDealId: initialRelated = "",
  relatedLabel = "",
  relatedReadOnly = false,
  relatedFieldLabel = "Related loan",
  relatedHelp = "",
  onCreated,
  onCancel,
  submitLabel = "Submit Ticket",
  autoFocusSubject = false,
  bodyPadding = "28px",
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { isAdmin } = usePermissionsHook();

  const [meta, setMeta] = useState(null);
  const [category, setCategory] = useState("");
  const [severity, setSeverity] = useState(DEFAULT_SEVERITY);
  const [subject, setSubject] = useState("");
  // HtmlEditor onChange object, kept uncontrolled (see the spaces fix).
  const [description, setDescription] = useState(null);
  const [relatedDealId, setRelatedDealId] = useState(initialRelated);
  const [relatedCleared, setRelatedCleared] = useState(false);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supportTicketsApi
      .getMeta()
      .then((m) => {
        if (!cancelled) setMeta(m);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  // /meta drives the list when present; admin-only categories are appended
  // client-side (the backend doesn't advertise or enforce them).
  const categories = useMemo(
    () =>
      categoriesForUser(meta?.categories, isAdmin).map(
        (v) =>
          CATEGORY_CARDS.find((c) => c.value === v) || {
            value: v,
            icon: ReportProblemOutlined,
            desc: "",
          },
      ),
    [meta, isAdmin],
  );
  const severities = meta?.severities?.length
    ? meta.severities
    : SUPPORT_TICKET_SEVERITIES;

  const isFeatureRequest = category === FEATURE_REQUEST_CATEGORY;

  const showChip = relatedReadOnly && initialRelated && !relatedCleared;

  const submit = async () => {
    setError(null);
    if (!category) return setError("Please choose a category.");
    if (!subject.trim()) return setError("Subject is required.");
    setBusy(true);
    try {
      const ticket = await supportTicketsApi.create({
        subject: subject.trim(),
        description: description?.htmlString || "",
        category,
        severity,
        relatedDealId: relatedDealId || undefined,
      });
      enqueueSnackbar("Support ticket submitted.", { variant: "success" });
      onCreated?.(ticket);
    } catch (err) {
      setError(
        err?.response?.data?.detail ||
          err?.message ||
          "Failed to submit ticket.",
      );
      setBusy(false);
    }
  };

  return (
    <>
      <Box sx={{ p: bodyPadding }}>
        {/* Category */}
        <Box sx={{ mb: 4 }}>
          <FieldLabel required>Category</FieldLabel>
          <Box
            sx={{
              display: "grid",
              // Keep the rows balanced as the count changes: 3 cards sit in one
              // row, 4 (admin, with Feature Request) form a 2×2.
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2,1fr)",
                md:
                  categories.length % 2 === 0
                    ? "repeat(2,1fr)"
                    : "repeat(3,1fr)",
              },
              gap: 1.25,
            }}
          >
            {categories.map((c) => {
              const selected = category === c.value;
              const Icon = c.icon;
              return (
                <Box
                  key={c.value}
                  component="button"
                  type="button"
                  onClick={() => setCategory(c.value)}
                  sx={{
                    textAlign: "left",
                    font: "inherit",
                    border: `1px solid ${selected ? T.teal500 : T.line}`,
                    boxShadow: selected
                      ? `0 0 0 1px ${T.teal500} inset`
                      : "none",
                    background: selected ? T.teal50 : "#fff",
                    borderRadius: "8px",
                    p: "13px 14px",
                    cursor: "pointer",
                    transition: ".15s",
                    "&:hover": { borderColor: T.teal500 },
                  }}
                >
                  <Icon
                    sx={{
                      fontSize: 19,
                      color: T.ink700,
                      mb: "6px",
                      display: "block",
                    }}
                  />
                  <Box sx={{ fontWeight: 600, fontSize: 13, color: T.ink900 }}>
                    {c.value}
                  </Box>
                  {c.desc && (
                    <Box
                      sx={{
                        fontSize: 11.5,
                        color: T.ink500,
                        mt: "2px",
                        lineHeight: 1.35,
                      }}
                    >
                      {c.desc}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Severity — same field either way; a feature request reads as priority. */}
        <Box sx={{ mb: 4 }}>
          <FieldLabel>{isFeatureRequest ? "Priority" : "Severity"}</FieldLabel>
          <Box
            sx={{
              display: "inline-flex",
              border: `1px solid ${T.line}`,
              borderRadius: "8px",
              overflow: "hidden",
              background: "#fff",
              flexWrap: "wrap",
            }}
          >
            {severities.map((s) => {
              const on = severity === s;
              const m = SEVERITY_META[s] || SEVERITY_META.Low;
              return (
                <Box
                  key={s}
                  component="button"
                  type="button"
                  onClick={() => setSeverity(s)}
                  sx={{
                    font: "inherit",
                    fontSize: 13,
                    fontWeight: on ? 600 : 500,
                    color: on ? T.ink900 : T.ink500,
                    background: on ? T.teal50 : "transparent",
                    border: "none",
                    borderRight: `1px solid ${T.line}`,
                    "&:last-of-type": { borderRight: "none" },
                    px: "18px",
                    py: "9px",
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                    gap: "7px",
                  }}
                >
                  <Box
                    sx={{
                      width: 7,
                      height: 7,
                      borderRadius: "50%",
                      background: m.color,
                    }}
                  />
                  {s}
                </Box>
              );
            })}
          </Box>
        </Box>

        {/* Subject */}
        <Box sx={{ mb: 4 }}>
          <FieldLabel required>Subject</FieldLabel>
          <InputBase
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            autoFocus={autoFocusSubject}
            placeholder={
              isFeatureRequest
                ? "Short summary, e.g. “Add a bulk condition-clear action to the Conditions tab”"
                : "Short summary, e.g. “Wire instructions PDF fails to generate on closing tab”"
            }
            sx={{
              width: "100%",
              height: 40,
              px: "12px",
              fontSize: 14,
              border: `1px solid ${T.line}`,
              borderRadius: "8px",
              background: "#fff",
              "&.Mui-focused": { borderColor: T.teal500 },
            }}
          />
        </Box>

        {/* Description */}
        <Box sx={{ mb: 4 }}>
          <FieldLabel required>Description</FieldLabel>
          <HtmlEditor
            value={description}
            onChange={setDescription}
            enableImages
            minHeight="140px"
            maxHeight="320px"
            placeholder={
              isFeatureRequest
                ? "Describe the workflow today, what you want instead, and who it helps. Paste mockups or screenshots directly into this field."
                : "Describe what happened, what you expected, and steps to reproduce. Paste screenshots directly into this field."
            }
          />
        </Box>

        {/* Related loan / route */}
        <Box>
          <FieldLabel optional>{relatedFieldLabel}</FieldLabel>
          {relatedHelp && (
            <Box
              sx={{
                fontSize: 12,
                color: T.ink500,
                mb: "8px",
                lineHeight: 1.45,
              }}
            >
              {relatedHelp}
            </Box>
          )}
          {showChip ? (
            <Box
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "8px",
                background: T.teal50,
                border: `1px solid ${T.teal100}`,
                color: T.teal600,
                borderRadius: "999px",
                p: "6px 8px 6px 12px",
                fontSize: 12.5,
                fontWeight: 600,
                maxWidth: "100%",
              }}
            >
              <Box
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {relatedLabel || initialRelated}
              </Box>
              <Box
                component="button"
                type="button"
                onClick={() => {
                  setRelatedDealId("");
                  setRelatedCleared(true);
                }}
                sx={{
                  width: 18,
                  height: 18,
                  borderRadius: "50%",
                  background: "#fff",
                  border: "none",
                  display: "grid",
                  placeItems: "center",
                  cursor: "pointer",
                  color: T.ink500,
                  flexShrink: 0,
                }}
              >
                <CloseRounded sx={{ fontSize: 11 }} />
              </Box>
            </Box>
          ) : (
            <InputBase
              value={relatedDealId}
              onChange={(e) => setRelatedDealId(e.target.value)}
              placeholder="Loan / deal ID (optional)"
              sx={{
                width: "100%",
                height: 40,
                px: "12px",
                fontSize: 14,
                border: `1px solid ${T.line}`,
                borderRadius: "8px",
                background: "#fff",
                "&.Mui-focused": { borderColor: T.teal500 },
              }}
            />
          )}
        </Box>

        {error && (
          <Alert severity="error" sx={{ mt: 2.5 }}>
            {error}
          </Alert>
        )}
      </Box>

      {/* Actions */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          p: "18px 28px",
          borderTop: `1px solid ${T.line}`,
          background: "#fbfdfe",
          flexWrap: "wrap",
        }}
      >
        <Button
          variant="contained"
          onClick={submit}
          disabled={busy}
          sx={{
            background: T.teal600,
            "&:hover": { background: "#127f97" },
            borderRadius: "8px",
          }}
        >
          {busy ? "Submitting…" : submitLabel}
        </Button>
        {onCancel && (
          <Button
            variant="outlined"
            color="inherit"
            onClick={onCancel}
            disabled={busy}
            sx={{ borderColor: T.line, color: T.ink700, borderRadius: "8px" }}
          >
            Cancel
          </Button>
        )}
        <Box sx={{ ml: "auto", fontSize: 12, color: T.ink500 }}>
          🎫 Support is notified in Teams on submit
        </Box>
      </Box>
    </>
  );
};

export default SupportTicketForm;
